/**
 * HelpCenterPage - FAQ + support contact for NLT Radio
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, Mail, LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FAQS = [
  {
    q: 'What is NLT Radio?',
    a: 'NLT Radio is the music side of NoLiesTold — a place for independent artists to drop music, build a fanbase, and earn directly from sales, tips, and merch. Your NLT and NLT Radio account are the same account.',
  },
  {
    q: 'Do I need an account to listen?',
    a: 'You can play 10-second snippets without signing up. To keep listening and support the artist, create a free account (email or Google). Signing up automatically links your NoLiesTold account.',
  },
  {
    q: 'How do I become an artist?',
    a: 'During profile setup choose "Artist". This unlocks your Artist Dashboard, uploads, and your live room. You can switch from a listener profile at any time in Settings → Edit Profile.',
  },
  {
    q: 'How does voting work?',
    a: 'Under every song you can react with Flop, Issa Hit, or Rocking With It. Your net score (upvotes minus flops) pushes songs into rotation and boosts artist visibility across the platform.',
  },
  {
    q: 'What are the album cover requirements?',
    a: 'Album covers must be square and at least 3000x3000px (industry standard). Uploads that don\'t meet this are rejected. Every song upload also requires credits: songwriter, composer, producer, and vocalist.',
  },
  {
    q: 'How do artists get paid?',
    a: 'Artists earn through direct transactions — song/album sales, tips (Support Artist), and merch — processed via Stripe. NLT takes a small platform fee on transactions.',
  },
  {
    q: 'Are my follows and messages shared with NoLiesTold?',
    a: 'Yes. NLT Radio and NoLiesTold share the same accounts, follows, friends, and messages. People you follow or friend on either app show up in your network on both.',
  },
  {
    q: 'How do interface layouts work?',
    a: 'In Settings → Player Layout you can choose Live Room, Classic Player, Feed View, or Focus Mode. Your choice is saved to your profile and applied everywhere you log in.',
  },
];

const FaqItem = ({ item, isOpen, onToggle, index }) => (
  <div className="bg-[#12101A] rounded-xl border border-white/5 overflow-hidden" data-testid={`faq-${index}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-white/5 transition-colors"
    >
      <span className="text-white font-medium">{item.q}</span>
      <ChevronDown
        className={`w-5 h-5 text-[#71717A] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    {isOpen && (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        className="px-4 pb-4"
      >
        <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.a}</p>
      </motion.div>
    )}
  </div>
);

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="help-center-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="help-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
            Help Center
          </h1>
        </div>
      </div>

      <div className="px-4 py-6 space-y-6">
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#FF007F]/15 to-[#00F0FF]/10 border border-white/5">
          <LifeBuoy className="w-8 h-8 text-[#FF007F] flex-shrink-0" />
          <div>
            <p className="text-white font-semibold">How can we help?</p>
            <p className="text-[#71717A] text-sm">Answers to common questions about NLT Radio.</p>
          </div>
        </div>

        <div className="space-y-2">
          {FAQS.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </div>

        {/* Contact */}
        <div className="bg-[#12101A] rounded-2xl border border-white/5 p-5 text-center">
          <h3 className="text-white font-semibold mb-1">Still need help?</h3>
          <p className="text-[#71717A] text-sm mb-4">Reach our support team and we'll get back to you.</p>
          <a
            href="mailto:support@nltradio.com"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#00F0FF] text-black font-medium hover:bg-[#00F0FF]/90 transition-colors"
            data-testid="help-contact-btn"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
